/**
 * Stage 5 — The test debate.
 *
 * The conversion moment. The user asks their freshly-built brain a question
 * it has never seen. They read the answer. They decide whether it sounds
 * like them.
 *
 * Phase 1 ships only Part B (test debate). Part A (the "Your brain in 5
 * moves" reveal page) lands in Phase 2.
 *
 * Pipes through the existing /api/worldview/chat endpoint by sending the
 * assembled system prompt as the `worldviewContent` field — no backend
 * changes needed for the kill-switch spike.
 */

import {
  type FormEvent,
  type KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import type {
  ChatMessage,
  StructuredWorldview,
  WorldviewChatRequest,
  WorldviewChatResponse,
} from '@arena/shared';
import { assembleSystemPrompt } from '../../lib/systemPromptAssembler';

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

/**
 * Suggested test questions — deliberately drawn from domains OUTSIDE the
 * Phase 1 question bank (ethics + epistemics). These probe whether the
 * brain can extrapolate to genuinely-unseen territory.
 */
const SUGGESTED_QUESTIONS: string[] = [
  'What makes a piece of writing actually good?',
  'How would you decide between two job offers — one safer, one more ambitious?',
  "Is it possible to genuinely change at 35? At 60?",
  'What is the most overrated virtue?',
  'When does a useful habit cross over into a trap?',
  'What kind of person do you find it hardest to take seriously?',
];

interface Stage5Props {
  worldviewName: string;
  preview: StructuredWorldview;
  /** Called when the user decides to save the brain to the library. */
  onSave: () => void;
  /** Called when the user wants to keep tuning before saving. */
  onRefine: () => void;
  /** Called when the user wants to bail entirely. */
  onExit: () => void;
}

export function Stage5TestDebate({
  worldviewName,
  preview,
  onSave,
  onRefine,
  onExit,
}: Stage5Props) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const transcriptRef = useRef<HTMLDivElement>(null);

  // Assemble the system prompt once per preview change (≤30 cases — cheap).
  const systemPrompt = (() => {
    try {
      return assembleSystemPrompt({ worldview: preview });
    } catch {
      return '';
    }
  })();

  useEffect(() => {
    const el = transcriptRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages, pending]);

  const send = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || pending) return;
    setError(null);

    const next: ChatMessage[] = [
      ...messages,
      { role: 'user', content: trimmed },
    ];
    setMessages(next);
    setInput('');
    setPending(true);

    try {
      const body: WorldviewChatRequest = {
        worldviewContent: systemPrompt,
        messages: next,
      };
      const res = await fetch(`${API_BASE}/api/worldview/chat`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const payload = (await res.json().catch(() => ({}))) as {
          error?: string;
          detail?: string;
        };
        throw new Error(
          payload.detail ?? payload.error ?? `HTTP ${res.status}`,
        );
      }
      const data = (await res.json()) as WorldviewChatResponse;
      setMessages((m) => [...m, data.message]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Request failed');
    } finally {
      setPending(false);
      window.setTimeout(() => inputRef.current?.focus(), 60);
    }
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    void send(input);
  };

  const handleKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void send(input);
    }
  };

  const handleSuggested = (q: string) => {
    if (pending) return;
    setInput(q);
    void send(q);
  };

  const hasConversation = messages.length > 0 || pending;

  return (
    <div className="onboarding-stage5">
      <header className="onboarding-stage5__header">
        <p className="onboarding-stage5__eyebrow">— The test —</p>
        <h2 className="onboarding-stage5__title">
          Ask <em>{worldviewName}</em> a question it&rsquo;s never seen.
        </h2>
        <p className="onboarding-stage5__lede">
          Built from {preview.cases.length} cases. The questions below come
          from domains your brain wasn&rsquo;t trained on. Read the answer.
          Does it sound like you?
        </p>
      </header>

      {!hasConversation && (
        <div className="onboarding-stage5__suggestions">
          <p className="onboarding-stage5__suggestions-label">
            — Pick one, or write your own —
          </p>
          <ul className="onboarding-stage5__suggestions-list">
            {SUGGESTED_QUESTIONS.map((q) => (
              <li key={q}>
                <button
                  type="button"
                  className="onboarding-stage5__suggestion"
                  onClick={() => handleSuggested(q)}
                >
                  {q}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {hasConversation && (
        <div
          className="onboarding-stage5__transcript"
          ref={transcriptRef}
          aria-live="polite"
        >
          {messages.map((m, i) => (
            <article
              key={i}
              className={`onboarding-stage5__bubble onboarding-stage5__bubble--${m.role}`}
            >
              <header className="onboarding-stage5__bubble-name">
                {m.role === 'user' ? 'You' : worldviewName}
              </header>
              <div className="onboarding-stage5__bubble-body">{m.content}</div>
            </article>
          ))}
          {pending && (
            <article className="onboarding-stage5__bubble onboarding-stage5__bubble--assistant onboarding-stage5__bubble--pending">
              <header className="onboarding-stage5__bubble-name">
                {worldviewName}
              </header>
              <div className="onboarding-stage5__bubble-body">
                <span className="onboarding-stage5__dots" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </span>
              </div>
            </article>
          )}
          {error && (
            <div className="onboarding-stage5__error" role="alert">
              {error}
            </div>
          )}
        </div>
      )}

      <form className="onboarding-stage5__composer" onSubmit={handleSubmit}>
        <textarea
          ref={inputRef}
          className="onboarding-stage5__input"
          placeholder={
            hasConversation
              ? 'Ask another question…'
              : `Ask ${worldviewName} anything…`
          }
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKey}
          rows={1}
          disabled={pending}
        />
        <button
          type="submit"
          className="onboarding-stage5__send"
          disabled={pending || input.trim().length === 0}
          aria-label="Send"
        >
          <span aria-hidden="true">→</span>
        </button>
      </form>

      <footer className="onboarding-stage5__verdict">
        <p className="onboarding-stage5__verdict-q">Sound like you?</p>
        <div className="onboarding-stage5__verdict-actions">
          <button
            type="button"
            className="onboarding-stage5__refine"
            onClick={onRefine}
          >
            Not quite — let me tune
          </button>
          <button
            type="button"
            className="onboarding-stage5__save"
            onClick={onSave}
            disabled={messages.length === 0}
          >
            Save this brain <span aria-hidden="true">→</span>
          </button>
        </div>
        <button
          type="button"
          className="onboarding-stage5__bail"
          onClick={onExit}
        >
          Discard this attempt
        </button>
      </footer>
    </div>
  );
}
